/**
 * Route Tracing
 *
 * Datadog RUM implementation for tracing route execution phases.
 * Uses two complementary mechanisms:
 *
 * 1. Custom Vitals (startDurationVital/stopDurationVital)
 *    - Purpose: Duration measurement for dashboards
 *    - Query: @type:vital @vital.name:route.*
 *    - Metric: @vital.duration (native percentiles p50, p95, p99)
 *
 * 2. Custom Actions (addAction)
 *    - Purpose: Event markers for debugging, linked to XHRs/errors
 *    - Query: @type:action @action.name:route.*
 *    - Shows in session timeline for debugging
 *
 * Phases tracked: gas_station, unshield, runner_step, transfer_from_autopilot
 */

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import type {
  PhaseMeta,
  RoutePhase,
  RouteStatus,
  RouteTraceMeta,
  RouteTracer,
} from '@polaris/core/machines'

interface RouteContext {
  route_id: string
  source_chain?: string
  dest_chain?: string
  is_privacy_route: boolean
}

// Track active phases with their start time (for action duration calculation)
const activePhases = new Map<string, number>()

// Track route start times and metadata
const routeStartTimes = new Map<string, number>()
const routeMeta = new Map<string, { is_privacy_route: boolean }>()

// Track vital references for proper stop calls
// biome-ignore lint/suspicious/noExplicitAny: DD vital ref is opaque
const activeVitals = new Map<string, any>()

function makePhaseKey(
  routeId: string,
  phase: RoutePhase,
  step?: number,
): string {
  return `${routeId}:${phase}:${step ?? 0}`
}

function isDatadogAvailable(): boolean {
  try {
    return datadogRum.getInternalContext() !== undefined
  } catch {
    return false
  }
}

/**
 * Start tracing a route execution.
 * Sets global context so all XHRs during the route are tagged with route_id.
 */
function startRouteSpan(ctx: RouteContext): void {
  if (!isDatadogAvailable()) return

  routeStartTimes.set(ctx.route_id, Date.now())
  routeMeta.set(ctx.route_id, { is_privacy_route: ctx.is_privacy_route })

  // Set global context so all resources/errors are tagged
  datadogRum.setGlobalContextProperty('route_id', ctx.route_id)
  datadogRum.setGlobalContextProperty('is_privacy_route', ctx.is_privacy_route)
  datadogLogs.setGlobalContextProperty('route_id', ctx.route_id)
  datadogLogs.setGlobalContextProperty('is_privacy_route', ctx.is_privacy_route)

  // Start vital for duration measurement (dashboards)
  const vitalRef = datadogRum.startDurationVital('route', {
    context: ctx,
    description: ctx.is_privacy_route ? 'privacy' : 'standard',
  })
  activeVitals.set(ctx.route_id, vitalRef)

  // Action for debugging (session timeline)
  datadogRum.addAction('route.start', ctx)
}

/**
 * Stop tracing a route execution.
 * Clears global context and emits final status with duration.
 */
function stopRouteSpan(ctx: RouteContext, status: RouteStatus): void {
  if (!isDatadogAvailable()) return

  const startTime = routeStartTimes.get(ctx.route_id)

  // Skip tracing for resumed routes (page refresh) - we can't measure what we didn't start
  if (!startTime) return

  const duration_ms = Date.now() - startTime
  routeStartTimes.delete(ctx.route_id)
  routeMeta.delete(ctx.route_id)

  // Stop vital for duration measurement
  const vitalRef = activeVitals.get(ctx.route_id)
  if (vitalRef) {
    datadogRum.stopDurationVital(vitalRef)
    activeVitals.delete(ctx.route_id)
  }

  // Action for debugging (session timeline)
  datadogRum.addAction('route.end', { ...ctx, status, duration_ms })

  // Clear global context
  datadogRum.removeGlobalContextProperty('route_id')
  datadogRum.removeGlobalContextProperty('is_privacy_route')
  datadogLogs.removeGlobalContextProperty('route_id')
  datadogLogs.removeGlobalContextProperty('is_privacy_route')

  // Clean up any orphaned phases/vitals for this route
  const routePrefix = `${ctx.route_id}:`
  for (const key of activePhases.keys()) {
    if (key.startsWith(routePrefix)) {
      activePhases.delete(key)
    }
  }
  for (const [key, vitalRef] of activeVitals.entries()) {
    if (key.startsWith(routePrefix)) {
      // Stop orphaned vitals before cleanup to avoid missing data/leaks
      datadogRum.stopDurationVital(vitalRef)
      activeVitals.delete(key)
    }
  }
}

/**
 * Start tracing a specific phase within the route.
 */
function startPhaseSpan(
  phase: RoutePhase,
  ctx: RouteContext,
  meta?: PhaseMeta,
): void {
  if (!isDatadogAvailable()) return

  const phaseKey = makePhaseKey(ctx.route_id, phase, meta?.step)

  // Prevent duplicate starts (e.g., on retry without proper cleanup)
  if (activePhases.has(phaseKey)) {
    console.warn(`[route-tracing] Phase already active: ${phaseKey}`)
    return
  }
  activePhases.set(phaseKey, Date.now())

  // Start vital for duration measurement (dashboards)
  const vitalRef = datadogRum.startDurationVital(`route.${phase}`, {
    context: { ...ctx, step: meta?.step, phase },
    description: ctx.route_id,
  })
  activeVitals.set(phaseKey, vitalRef)

  // Action for debugging (session timeline)
  datadogRum.addAction(`route.${phase}.start`, {
    ...ctx,
    step: meta?.step,
    phase,
  })
}

/**
 * Stop tracing a specific phase with duration.
 */
function stopPhaseSpan(
  phase: RoutePhase,
  ctx: RouteContext,
  status: 'success' | 'failure',
  meta?: PhaseMeta,
): void {
  if (!isDatadogAvailable()) return

  const phaseKey = makePhaseKey(ctx.route_id, phase, meta?.step)
  const startTime = activePhases.get(phaseKey)

  if (!startTime) {
    // Phase was never started or already stopped - just emit an event
    datadogRum.addAction(`route.${phase}.end`, {
      ...ctx,
      step: meta?.step,
      phase,
      status,
      orphaned: true,
    })
    return
  }

  const duration_ms = Date.now() - startTime
  activePhases.delete(phaseKey)

  // Stop vital for duration measurement
  const vitalRef = activeVitals.get(phaseKey)
  if (vitalRef) {
    datadogRum.stopDurationVital(vitalRef)
    activeVitals.delete(phaseKey)
  }

  // Action for debugging (session timeline)
  datadogRum.addAction(`route.${phase}.end`, {
    ...ctx,
    step: meta?.step,
    phase,
    status,
    duration_ms,
  })
}

/**
 * Create a RouteTracer implementation backed by Datadog RUM.
 * Call this after Datadog RUM is initialized and pass the result
 * to setRouteTracer() from @polaris/core.
 */
export function createDatadogRouteTracer(): RouteTracer {
  return {
    startRoute(routeId: string, meta: RouteTraceMeta): void {
      startRouteSpan({
        route_id: routeId,
        source_chain: meta.sourceChain,
        dest_chain: meta.destChain,
        is_privacy_route: meta.isPrivacy,
      })
    },

    stopRoute(routeId: string, status: RouteStatus): void {
      const meta = routeMeta.get(routeId)
      stopRouteSpan(
        {
          route_id: routeId,
          is_privacy_route: meta?.is_privacy_route ?? false,
        },
        status,
      )
    },

    startPhase(routeId: string, phase: RoutePhase, meta?: PhaseMeta): void {
      const rm = routeMeta.get(routeId)
      startPhaseSpan(
        phase,
        { route_id: routeId, is_privacy_route: rm?.is_privacy_route ?? false },
        meta,
      )
    },

    stopPhase(
      routeId: string,
      phase: RoutePhase,
      status: 'success' | 'failure',
      meta?: PhaseMeta,
    ): void {
      const rm = routeMeta.get(routeId)
      stopPhaseSpan(
        phase,
        { route_id: routeId, is_privacy_route: rm?.is_privacy_route ?? false },
        status,
        meta,
      )
    },
  }
}
